import { useEffect, useMemo, useState } from 'react';
import {
  FiClock,
  FiEdit2,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiFilter,
  FiActivity,
  FiArrowUpCircle,
  FiArrowDownCircle,
  FiDownload,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import Api from '../../Api';
import { usePermission } from '../../hooks/usePermission';
import {
  Button,
  Card,
  Input,
  Modal,
  PageComponent,
  Skeletons,
  Table,
  TransactionForm,
  Select,
  MultiSelect,
  DatePeriodFilter,
  getDateParams,
  formatDateToISO,
  Tooltip
} from '../../components';
import '../CompanyManager/style.css';
import TableActions from '../../components/Table/TableActions';

import './style.css';

export default function TransactionManager() {
  const permissions = usePermission('financial');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [historyTransaction, setHistoryTransaction] = useState(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  
  // Filters State
  const [currentDate, setCurrentDate] = useState(new Date());
  const [periodMode, setPeriodMode] = useState('monthly'); // 'daily', 'weekly', 'monthly', 'annual', 'last12', 'all'
  const [customPeriod, setCustomPeriod] = useState({ startDate: '', endDate: '' });

  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedCostCenterId, setSelectedCostCenterId] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState('');
  const [selectedTypeFilter, setSelectedTypeFilter] = useState('');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('');
  const [selectedPartnerType, setSelectedPartnerType] = useState('');
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Options State
  const [partners, setPartners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [companies, setCompanies] = useState([]);

  const navigate = useNavigate();


  useEffect(() => {
    // Load initial data for all companies
    loadFiltersData('all');
  }, []);

  useEffect(() => {
    loadTransactions();
    // Also reset page when filters change
    setCurrentPage(1);
  }, [currentDate, periodMode, selectedCompanyFilter, selectedTypeFilter, selectedAccountId]);

  async function loadFiltersData(companyId) {
    try {
      const [custData, suppData, empData, catData, costData, accData, compData] = await Promise.all([
        Api.getCustomers(companyId),
        Api.getSuppliers(companyId),
        Api.getEmployees(companyId),
        Api.getCategories(companyId),
        Api.getCostCenters(companyId),
        Api.getAccounts(companyId),
        Api.getCompanies(),
      ]);

      const allPartners = [
        ...(Array.isArray(custData)
          ? custData.map(c => ({
              value: `customer-${c.id}`,
              label: `${c.name.toUpperCase()}`,
              type: 'Cliente',
              companyId: c.company_id,
              is_internal: !!c.is_internal,
            }))
          : []),
        ...(Array.isArray(suppData)
          ? suppData.map(s => ({
              value: `supplier-${s.id}`,
              label: `${s.name.toUpperCase()}`,
              type: 'Fornecedor',
              companyId: s.company_id,
              is_internal: !!s.is_internal,
            }))
          : []),
        ...(Array.isArray(empData)
          ? empData.map(e => ({
              value: `employee-${e.id}`,
              label: `${e.name.toUpperCase()}`,
              type: 'Funcionário',
              companyId: e.company_id,
            }))
          : []),
      ];
      setPartners(allPartners.sort((a, b) => a.label.localeCompare(b.label)));

      setCategories(Array.isArray(catData) ? catData.map(c => ({ value: c.id, label: c.name, companyId: c.company_id })) : []);
      setCostCenters(Array.isArray(costData) ? costData.map(c => ({ value: c.id, label: `${c.code || ''} ${c.name}`, companyId: c.company_id })) : []);
      setAccounts(Array.isArray(accData) ? accData.map(a => ({ value: a.id, label: a.name, companyId: a.company_id })) : []);

      let comps = [];
      if (Array.isArray(compData)) comps = compData;
      else if (compData && Array.isArray(compData.data)) comps = compData.data;
      else if (compData && compData.companies) comps = compData.companies; // subuser case maybe?

      setCompanies(comps.map(c => ({ value: String(c.id), label: c.trade_name || c.corporate_name || c.name })));

    } catch (error) {
      console.error('Error loading filters data:', error);
    }
  }

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedPartnerId, selectedCategoryId, selectedCostCenterId, selectedCompanyFilter, selectedTypeFilter, selectedStatusFilter]);

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = companyFilterValues;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map(c => String(c.value));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter(v => v !== allValue);
      const allIds = companies.map(c => String(c.value));
      if (specificIds.length === allIds.length && allIds.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }

    setCompanyFilterValues(nextValues);

    const idsWithoutAll = nextValues.filter(v => v !== allValue);
    if (idsWithoutAll.length === 1) {
      setSelectedCompanyFilter(idsWithoutAll[0]);
    } else {
      setSelectedCompanyFilter('');
    }
  }

  async function loadTransactions(companyId = 'all') {
    try {
      setLoading(true);
      
      const { startDate, endDate } = getDateParams(periodMode, periodMode === 'custom' ? customPeriod : currentDate);
      const params = {};
      if (startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }
      
      if (selectedTypeFilter) params.type = selectedTypeFilter;
      if (selectedAccountId) params.account_id = selectedAccountId;

      const response = await Api.getTransactions(companyId, params);
      console.log('API Response:', response);

      if (response && response.data && Array.isArray(response.data)) {
        console.log('Transactions Data:', response.data);
        setTransactions(response.data);
      } else if (Array.isArray(response)) {
        setTransactions(response);
      } else {
        setTransactions([]);
      }
      setHasLoadedOnce(true);
    } catch (err) {
      setError('Erro ao carregar transações. Tente novamente.');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }

  // ... handlers ...
  function handleCreate() {
    setSelectedTransaction(null);
    setIsModalOpen(true);
  }

  function handleEdit(transaction) {
    setSelectedTransaction(transaction);
    setIsModalOpen(true);
  }

  function handleFormSuccess() {
    loadTransactions();
    setIsModalOpen(false);
  }

  function handleDeleteClick(transaction) {
    setTransactionToDelete(transaction);
    setIsDeleteModalOpen(true);
  }

  async function handleShowHistory(transaction) {
    setHistoryTransaction(transaction);
    setIsHistoryModalOpen(true);
    setHistoryLoading(true);
    setHistoryError('');
    setHistoryItems([]);

    try {
      const response = await Api.getTransactionHistory(transaction.company_id, transaction.id);

      if (response?.error) {
        setHistoryError(response.error);
        return;
      }

      if (response?.transaction) {
        setHistoryTransaction((prev) => ({
          ...prev,
          ...response.transaction,
        }));
      }

      setHistoryItems(Array.isArray(response?.history) ? response.history : []);
    } catch (error) {
      setHistoryError('Erro ao carregar histórico do lançamento.');
    } finally {
      setHistoryLoading(false);
    }
  }

  function formatAuditDateTime(value) {
    if (!value) return '-';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';

    return date.toLocaleString('pt-BR');
  }

  function formatAuditRole(role) {
    if (!role) return '';
    if (role === 'subuser') return 'Subusuário';
    if (role === 'users' || role === 'admin') return 'Administrador';
    return 'Usuário';
  }

  function formatAuditPerson(name, role) {
    if (!name) return '-';

    const roleLabel = formatAuditRole(role);
    return roleLabel ? `${name} (${roleLabel})` : name;
  }

  function formatHistoryAction(action) {
    if (action === 'created') return 'Criou o lançamento';
    if (action === 'updated') return 'Alterou o lançamento';
    if (action === 'imported') return 'Importou o lançamento';
    if (action === 'reconciled') return 'Conciliou o lançamento';
    return 'Atualizou o lançamento';
  }

  function formatHistoryValue(value, field) {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'boolean') return value ? 'Sim' : 'Não';

    const stringValue = String(value);

    const statusMap = {
      pending: 'Pendente',
      paid: 'Pago',
      late: 'Atrasado',
      cancelled: 'Cancelado',
    };

    const typeMap = {
      revenue: 'Receita',
      expense: 'Despesa',
    };

    const paymentMethodMap = {
      PIX: 'Pix',
      BOLETO: 'Boleto',
      TRANSFER: 'Transferência',
      DEBIT_CARD: 'Cartão de Débito',
    };

    if (field === 'status' && statusMap[stringValue]) {
      return statusMap[stringValue];
    }

    if (field === 'type' && typeMap[stringValue]) {
      return typeMap[stringValue];
    }

    if (field === 'payment_method' && paymentMethodMap[stringValue]) {
      return paymentMethodMap[stringValue];
    }

    if (['_date', 'date'].some((token) => field && field.includes(token))) {
      const date = new Date(`${stringValue}T12:00:00`);
      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleDateString('pt-BR');
      }
    }

    return stringValue;
  }

  async function handleConfirmDelete() {
    if (!transactionToDelete) return;

    try {
      setIsDeleting(true);
      await Api.deleteTransaction(transactionToDelete.company_id, transactionToDelete.id);
      loadTransactions();
      setIsDeleteModalOpen(false);
      setTransactionToDelete(null);
    } catch (err) {
      console.error(err);
      alert('Erro ao excluir transação.');
    } finally {
      setIsDeleting(false);
    }
  }

  const filteredTransactions = useMemo(() => {
    const allValue = 'all';
    const specificIds = companyFilterValues.filter(v => v !== allValue);
    const hasAllSelected = companyFilterValues.includes(allValue);

    return transactions.filter(transaction => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        (transaction.description && transaction.description.toLowerCase().includes(searchLower)) ||
        (transaction.reference_code && transaction.reference_code.toLowerCase().includes(searchLower));

      // Filter by Month/Year (Competence Date)
      // Server-side filtering is used, so we skip strict client-side date filtering to avoid timezone issues
      const matchesDate = true;

      // Filter by Partner Type
      let matchesPartnerType = true;
      if (selectedPartnerType) {
        if (selectedPartnerType === 'customer') matchesPartnerType = !!transaction.customer_id;
        else if (selectedPartnerType === 'supplier') matchesPartnerType = !!transaction.supplier_id;
        else if (selectedPartnerType === 'employee') matchesPartnerType = !!transaction.employee_id;
        else if (selectedPartnerType === 'internal') {
          const isCustomerInternal = transaction.customer && transaction.customer.is_internal;
          const isSupplierInternal = transaction.supplier && transaction.supplier.is_internal;
          matchesPartnerType = !!(isCustomerInternal || isSupplierInternal);
        }
      }

      // Filter by Partner
      let matchesPartner = true;
      if (selectedPartnerId) {
        if (selectedPartnerId.startsWith('customer-')) {
          const id = selectedPartnerId.replace('customer-', '');
          matchesPartner = String(transaction.customer_id) === String(id);
        } else if (selectedPartnerId.startsWith('supplier-')) {
          const id = selectedPartnerId.replace('supplier-', '');
          matchesPartner = String(transaction.supplier_id) === String(id);
        } else if (selectedPartnerId.startsWith('employee-')) {
          const id = selectedPartnerId.replace('employee-', '');
          matchesPartner = String(transaction.employee_id) === String(id);
        } else {
          const pId = transaction.customer_id || transaction.supplier_id || transaction.employee_id;
          matchesPartner = String(pId) === String(selectedPartnerId);
        }
      }

      // Filter by Category
      let matchesCategory = true;
      if (selectedCategoryId) {
        matchesCategory = String(transaction.category_id) === String(selectedCategoryId);
      }

      // Filter by Cost Center
      let matchesCostCenter = true;
      if (selectedCostCenterId) {
        matchesCostCenter = String(transaction.cost_center_id) === String(selectedCostCenterId);
      }

      // Filter by Account
      let matchesAccount = true;
      if (selectedAccountId) {
        matchesAccount = String(transaction.account_id) === String(selectedAccountId);
      }

      let matchesStatus = true;
      if (selectedStatusFilter) {
        const isPaid = transaction.status === 'paid';
        if (selectedStatusFilter === 'paid') {
          matchesStatus = isPaid;
        } else if (selectedStatusFilter === 'pending') {
          matchesStatus = !isPaid;
        }
      }

      const companyId = transaction.company_id;
      const matchesCompany =
        hasAllSelected ||
        specificIds.length === 0 ||
        specificIds.includes(String(companyId));

      return matchesSearch && matchesDate && matchesPartnerType && matchesPartner && matchesCategory && matchesCostCenter && matchesCompany && matchesAccount && matchesStatus;
    }).sort((a, b) => {
      // Sort by due_date ascending (earliest first) - User requested "recent" (2024) to be on first page, implying Ascending order
      const dateA = a.due_date || a.date || '';
      const dateB = b.due_date || b.date || '';
      // Use localeCompare for reliable string comparison of ISO dates
      const dateComparison = dateA.localeCompare(dateB);
      
      // If dates are equal, sort by ID descending (newest created first)
      if (dateComparison !== 0) {
        return dateComparison;
      }
      return b.id - a.id;
    });
  }, [transactions, searchTerm, currentDate, selectedPartnerId, selectedCategoryId, selectedCostCenterId, selectedPartnerType, selectedAccountId, companyFilterValues, selectedStatusFilter]);

  const filteredPartnersOptions = useMemo(() => {
    // First filter by company if selected
    let filtered = partners;
    if (selectedCompanyFilter) {
      filtered = filtered.filter(p => String(p.companyId) === String(selectedCompanyFilter));
    }

  if (!selectedPartnerType) return [{ value: '', label: 'TODOS' }, ...filtered];

  if (selectedPartnerType === 'internal') {
    // Interno: somente clientes/fornecedores marcados como internos
    filtered = filtered.filter(
      (p) =>
        (p.type === 'Cliente' || p.type === 'Fornecedor') &&
        p.is_internal
    );
  } else if (selectedPartnerType === 'customer') {
    // Cliente: somente não internos
    filtered = filtered.filter(
      (p) =>
        p.type === 'Cliente' &&
        !p.is_internal
    );
  } else if (selectedPartnerType === 'supplier') {
    // Fornecedor: somente não internos
    filtered = filtered.filter(
      (p) =>
        p.type === 'Fornecedor' &&
        !p.is_internal
    );
  } else if (selectedPartnerType === 'employee') {
    filtered = filtered.filter((p) => p.type === 'Funcionário');
  }
    return [{ value: '', label: 'TODOS' }, ...filtered];
  }, [partners, selectedPartnerType, selectedCompanyFilter]);

  const filteredCategoryOptions = useMemo(() => {
    let filtered = categories;
    if (selectedCompanyFilter) {
      filtered = filtered.filter(c => String(c.companyId) === String(selectedCompanyFilter));
    }
    return [{ value: '', label: 'TODAS' }, ...filtered];
  }, [categories, selectedCompanyFilter]);

  const filteredCostCenterOptions = useMemo(() => {
    let filtered = costCenters;
    if (selectedCompanyFilter) {
      filtered = filtered.filter(cc => String(cc.companyId) === String(selectedCompanyFilter));
    }
    return [{ value: '', label: 'TODOS' }, ...filtered];
  }, [costCenters, selectedCompanyFilter]);

  const filteredAccountOptions = useMemo(() => {
    let filtered = accounts;
    if (selectedCompanyFilter) {
      filtered = filtered.filter(acc => String(acc.companyId) === String(selectedCompanyFilter));
    }
    return [{ value: '', label: 'TODAS' }, ...filtered];
  }, [accounts, selectedCompanyFilter]);

  const totalRevenue = useMemo(() => {
    return filteredTransactions.reduce((sum, transaction) => {
      if (transaction.type !== 'revenue') return sum;
      const value = Number(transaction.value || 0);
      if (Number.isNaN(value)) return sum;
      return sum + value;
    }, 0);
  }, [filteredTransactions]);

  const totalExpense = useMemo(() => {
    return filteredTransactions.reduce((sum, transaction) => {
      if (transaction.type !== 'expense') return sum;
      const value = Number(transaction.value || 0);
      if (Number.isNaN(value)) return sum;
      return sum + value;
    }, 0);
  }, [filteredTransactions]);

  const totalItems = filteredTransactions.length;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedTransactions = filteredTransactions.slice(
    startIndex,
    startIndex + pageSize,
  );

  const formatCurrency = (val) => {
    return Number(val).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  function handleExport() {
    if (!filteredTransactions || filteredTransactions.length === 0) {
      toast.warning('Não há dados para exportar com os filtros atuais.');
      return;
    }

    const exportData = filteredTransactions.map(transaction => {
      const companyName = transaction.company
        ? (transaction.company.trade_name || transaction.company.corporate_name || '')
        : '';

      let partnerName = '';
      let partnerType = '';
      if (transaction.customer) {
        partnerName = transaction.customer.name || '';
        partnerType = 'Cliente';
      } else if (transaction.supplier) {
        partnerName = transaction.supplier.name || '';
        partnerType = 'Fornecedor';
      } else if (transaction.employee) {
        partnerName = transaction.employee.name || '';
        partnerType = 'Funcionário';
      }

      const isPaid = transaction.status === 'paid';
      const statusLabel = isPaid
        ? (transaction.type === 'revenue' ? 'Recebido' : 'Pago')
        : 'Pendente';

      return {
        'Empresa': companyName,
        'Tipo': transaction.type === 'revenue' ? 'Receita' : 'Despesa',
        'Descrição': transaction.description || '',
        'Código de Referência': transaction.referenceCode || '',
        'Tipo de Vínculo': partnerType,
        'Vínculo': partnerName,
        'Valor': Number(transaction.value || 0),
        'Status': statusLabel,
        'Data Vencimento': transaction.due_date
          ? new Date(transaction.due_date).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
          : '',
        'Data Pagamento': transaction.payment_date
          ? new Date(transaction.payment_date).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
          : '',
      };
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Lançamentos');
    XLSX.writeFile(wb, 'lancamentos_financeiros.xlsx');
  }

  const columns = [
    {
      id: 'type',
      header: 'Tipo',
      width: '100px',
      cellClassName: 'manager-table-cell',
      render: transaction => (
        <span className={`manager-tag ${transaction.type === 'revenue' ? 'manager-tag-success' : 'manager-tag-danger'}`}>
          {transaction.type === 'revenue' ? (
            <FiArrowUpCircle size={14} style={{ marginRight: 6 }} />
          ) : (
            <FiArrowDownCircle size={14} style={{ marginRight: 6 }} />
          )}
          {transaction.type === 'revenue' ? 'Receita' : 'Despesa'}
        </span>
      ),
    },
    {
      id: 'description',
      header: 'Descrição',
      width: 'minmax(0, 2fr)',
      cellClassName: 'manager-table-cell-primary',
      render: transaction => (
        <div className="manager-table-cell-text" style={{ maxWidth: '100%', overflow: 'hidden' }}>
          <Tooltip content={transaction.description} position="top">
            <span className="manager-table-title manager-table-description">
              {transaction.description}
            </span>
          </Tooltip>
          <span className="manager-table-subtitle">
            {transaction.referenceCode}
          </span>
        </div>
      ),
    },
    {
      id: 'partnerType',
      header: 'Tipo',
      width: 'minmax(0, 1fr)',
      cellClassName: 'manager-table-cell',
      render: transaction => {
        let type = '-';
        if (transaction.customer) {
          type = transaction.customer.is_internal ? 'INTERNO' : 'CLIENTE';
        } else if (transaction.supplier) {
          type = transaction.supplier.is_internal ? 'INTERNO' : 'FORNECEDOR';
        } else if (transaction.employee) {
          type = 'FUNCIONÁRIO';
        }

        return <span className="manager-table-subtitle">{type}</span>;
      },
    },
    {
      id: 'partnerName',
      header: 'Vínculo',
      width: 'minmax(0, 1.5fr)',
      cellClassName: 'manager-table-cell',
      render: transaction => {
        let name = '-';
        let isInternal = false;

        if (transaction.customer) {
          name = transaction.customer.name;
          isInternal = !!transaction.customer.is_internal;
        } else if (transaction.supplier) {
          name = transaction.supplier.name;
          isInternal = !!transaction.supplier.is_internal;
        } else if (transaction.employee) {
          name = transaction.employee.name;
        }

        return (
          <span className="manager-table-title" style={{ fontSize: '0.9rem' }}>
            {name}
            {isInternal && ' (Interno)'}
          </span>
        );
      },
    },
    {
      id: 'dueDate',
      header: 'Data Vencimento',
      width: 'minmax(0, 1fr)',
      cellClassName: 'manager-table-cell',
      render: transaction => (
        <span className="manager-table-subtitle">
          {transaction.due_date ? new Date(transaction.due_date).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '-'}
        </span>
      ),
    },
    {
      id: 'paymentDate',
      header: 'Data Pagamento',
      width: 'minmax(0, 1fr)',
      cellClassName: 'manager-table-cell',
      render: transaction => (
        <span className="manager-table-subtitle">
          {transaction.payment_date ? new Date(transaction.payment_date).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '-'}
        </span>
      ),
    },
    {
      id: 'value',
      header: 'Valor',
      width: 'minmax(0, 1fr)',
      cellClassName: 'manager-table-cell',
      render: transaction => (
        <span className={`manager-table-title ${transaction.type === 'revenue' ? 'text-success' : 'text-danger'}`}>
          {formatCurrency(transaction.value)}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      width: '100px',
      cellClassName: 'manager-table-cell',
      render: transaction => {
        const isPaid = transaction.status === 'paid';
        return (
          <span className={`manager-tag ${isPaid ? 'manager-tag-success' : 'manager-tag-warning'}`}>
            {isPaid ? (transaction.type === 'revenue' ? 'Recebido' : 'Pago') : 'Pendente'}
          </span>
        );
      },
    },
    {
      id: 'company',
      header: 'Empresa',
      width: 'minmax(0, 1fr)',
      cellClassName: 'manager-table-cell',
      render: transaction => (
        <span className="manager-table-subtitle">
          {transaction.company ? (transaction.company.trade_name || transaction.company.corporate_name) : '-'}
        </span>
      ),
    },
    {
        id: 'actions',
        header: 'Ações',
        width: '0.6fr',
        cellClassName: 'manager-table-cell manager-table-col-actions',
        render: transaction => (
        <TableActions>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleShowHistory(transaction);
            }}
          >
            <FiClock size={16} />
            Histórico
          </button>
          {permissions.canEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(transaction);
              }}
            >
              <FiEdit2 size={16} />
              Editar
            </button>
          )}
          {permissions.canDelete && (
            <button
              type="button"
              className="action-danger"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(transaction);
              }}
            >
              <FiTrash2 size={16} />
              Excluir
            </button>
          )}
        </TableActions>
      ),
      },
  ];

  function TransactionSkeletonWrapper() {
    const { CompanyManagerSkeleton } = Skeletons;
    return <CompanyManagerSkeleton />;
  }

  if (!permissions.canView) {
    return (
      <PageComponent authenticatedPage={true}>
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiActivity size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar esta página.</p>
              </div>
            </div>
          </div>
        </div>
      </PageComponent>
    );
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
        {loading && !hasLoadedOnce && <TransactionSkeletonWrapper />}

        {(hasLoadedOnce || !loading) && (
          <>
            <div className="manager-header">
              <div className="manager-header-title">
                <div className="manager-header-icon">
                  <FiActivity size={22} />
                </div>
                <div>
                  <h1>Receitas e Despesas</h1>
                  <p>
                    Gerencie os lançamentos financeiros da empresa.
                  </p>
                </div>
              </div>
              <div className="manager-header-actions" style={{ display: 'flex', gap: '8px' }}>
                <Button
                  onClick={handleExport}
                  className="manager-header-button"
                  variant="secondary"
                >
                  <FiDownload size={18} />
                  Exportar
                </Button>

                {permissions.canCreate && (
                  <Button onClick={handleCreate} className="manager-header-button">
                    <FiPlus size={18} />
                    Novo Lançamento
                  </Button>
                )}
              </div>
            </div>

            <Card className="manager-filters-card">
              <div className="manager-filters-header">
                <div className="manager-filters-title">
                  <FiFilter size={16} />
                  <span>Filtros</span>
                </div>
                {transactions.length > 0 && (
                  <span className="manager-filters-count">
                    {filteredTransactions.length} de {transactions.length} registros
                  </span>
                )}
              </div>

              <div className="tx-filter-container">
                <div className="tx-filter-item tx-filter-item--period">
                  <DatePeriodFilter
                    periodMode={periodMode}
                    setPeriodMode={setPeriodMode}
                    currentDate={currentDate}
                    setCurrentDate={setCurrentDate}
                    customRange={customPeriod}
                    setCustomRange={setCustomPeriod}
                  />
                </div>

                <div className="tx-filter-item tx-filter-item--company">
                  <label className="manager-filter-label">Empresa</label>
                  <MultiSelect
                    id="companyFilter"
                    options={[{ value: 'all', label: 'TODAS' }, ...companies]}
                    value={companyFilterValues}
                    onChange={handleCompanyFilterChange}
                    searchable
                  />
                </div>

                <div className="tx-filter-item tx-filter-item--search">
                  <Input
                    id="search"
                    label="Pesquisar"
                    placeholder="Buscar por descrição..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    icon={FiSearch}
                  />
                </div>

                <div className="tx-filter-item tx-filter-item--account">
                  <Select
                    id="accountFilter"
                    label="Conta Bancária"
                    options={filteredAccountOptions}
                    value={selectedAccountId}
                    onChange={(e) => setSelectedAccountId(e.target.value)}
                    searchable
                  />
                </div>

                <div className="tx-filter-item tx-filter-item--status">
                  <Select
                    id="statusFilter"
                    label="Status"
                    options={[
                      { value: '', label: 'TODOS' },
                      { value: 'pending', label: 'PENDENTE' },
                      { value: 'paid', label: 'OK' }
                    ]}
                    value={selectedStatusFilter}
                    onChange={(e) => setSelectedStatusFilter(e.target.value)}
                  />
                </div>

                <div className="tx-filter-item tx-filter-item--type">
                  <Select
                    id="typeFilter"
                    label="Tipo"
                    options={[
                        { value: '', label: 'TODOS' },
                        { value: 'revenue', label: 'Receita' },
                        { value: 'expense', label: 'Despesa' }
                    ]}
                    value={selectedTypeFilter}
                    onChange={(e) => setSelectedTypeFilter(e.target.value)}
                  />
                </div>

                <div className="tx-filter-item tx-filter-item--partner-type">
                  <Select
                    id="partnerTypeFilter"
                    label="Tipo de Vínculo"
                    options={[
                        { value: '', label: 'TODOS' },
                        { value: 'customer', label: 'Cliente' },
                        { value: 'supplier', label: 'Fornecedor' },
                        { value: 'employee', label: 'Funcionário' },
                        { value: 'internal', label: 'Interno' }
                    ]}
                    value={selectedPartnerType}
                    onChange={(e) => {
                        setSelectedPartnerType(e.target.value);
                        setSelectedPartnerId('');
                    }}
                  />
                </div>

                <div className="tx-filter-item tx-filter-item--partner">
                  <Select
                    id="partnerFilter"
                    label="Vínculo"
                    options={filteredPartnersOptions}
                    value={selectedPartnerId}
                    onChange={(e) => setSelectedPartnerId(e.target.value)}
                    searchable
                  />
                </div>

                <div className="tx-filter-item tx-filter-item--category">
                  <Select
                    id="categoryFilter"
                    label="Categoria"
                    options={filteredCategoryOptions}
                    value={selectedCategoryId}
                    onChange={(e) => setSelectedCategoryId(e.target.value)}
                    searchable
                  />
                </div>

                <div className="tx-filter-item tx-filter-item--cost-center">
                  <Select
                    id="costCenterFilter"
                    label="Centro de Custo"
                    options={filteredCostCenterOptions}
                    value={selectedCostCenterId}
                    onChange={(e) => setSelectedCostCenterId(e.target.value)}
                    searchable
                  />
                </div>
              </div>
            </Card>

            <div className="tx-summary-container">
              <div className="tx-summary-card tx-summary-card--expense">
                <span className="tx-summary-label">Total Despesas</span>
                <span className="tx-summary-value tx-summary-value--expense">
                  {formatCurrency(totalExpense)}
                </span>
              </div>
              <div className="tx-summary-card tx-summary-card--revenue">
                <span className="tx-summary-label">Total Receitas</span>
                <span className="tx-summary-value tx-summary-value--revenue">
                  {formatCurrency(totalRevenue)}
                </span>
              </div>
            </div>

            {error ? (
              <div className="manager-error-container">
                <p>{error}</p>
                <Button onClick={() => loadTransactions()}>
                  Tentar Novamente
                </Button>
              </div>
            ) : (
              <Table
                items={paginatedTransactions}
                columns={columns}
                totalItems={totalItems}
                page={safePage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={setPageSize}
                pageSizeOptions={[5, 10, 12, 20, 50, 100, 500, 1000]}
                getRowKey={(t) => t.id}
                emptyMessage="Nenhum lançamento encontrado."
              />
            )}
          </>
        )}
      </div>

      {isModalOpen && (
        <Modal
          title={selectedTransaction ? 'Editar Lançamento' : 'Novo Lançamento'}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          size="lg"
        >
          <TransactionForm
            companyId={selectedCompanyFilter || ''}
            initialData={selectedTransaction}
            onSuccess={handleFormSuccess}
            onCancel={() => setIsModalOpen(false)}
            companies={companies}
          />
        </Modal>
      )}

      {isDeleteModalOpen && (
        <Modal
          title="Confirmar Exclusão"
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
        >
          <div className="delete-modal-content">
            <p>
              Tem certeza que deseja excluir o lançamento{' '}
              <span className="delete-modal-company-name">
                {transactionToDelete?.description}
              </span>
              ?
            </p>
            <p>Essa ação não poderá ser desfeita.</p>
          </div>
          <div className="delete-modal-actions">
            <Button
              type="button"
              className="cancel-button"
              onClick={() => setIsDeleteModalOpen(false)}
              disabled={isDeleting}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Excluindo...' : 'Excluir'}
            </Button>
          </div>
        </Modal>
      )}

      {isHistoryModalOpen && (
        <Modal
          title="Histórico do Lançamento"
          isOpen={isHistoryModalOpen}
          onClose={() => setIsHistoryModalOpen(false)}
        >
          <div className="transaction-history-modal">
            <div className="transaction-history-header">
              <span className="transaction-history-title">
                {historyTransaction?.description || 'Lançamento'}
              </span>
              <span className="transaction-history-subtitle">
                {historyItems.length} evento{historyItems.length === 1 ? '' : 's'}
              </span>
            </div>

            {historyLoading && (
              <div className="transaction-history-loading">
                <div className="transaction-history-card">
                  <div className="transaction-history-card-header">
                    <Skeletons.Skeleton width="180px" height="18px" />
                    <Skeletons.Skeleton width="120px" height="14px" />
                  </div>
                  <Skeletons.Skeleton width="220px" height="14px" />
                  <Skeletons.Skeleton count={2} height="12px" />
                </div>

                <div className="transaction-history-card">
                  <div className="transaction-history-card-header">
                    <Skeletons.Skeleton width="160px" height="18px" />
                    <Skeletons.Skeleton width="120px" height="14px" />
                  </div>
                  <Skeletons.Skeleton width="240px" height="14px" />
                  <Skeletons.Skeleton count={3} height="12px" />
                </div>
              </div>
            )}

            {!historyLoading && historyError && (
              <div className="transaction-history-empty">
                {historyError}
              </div>
            )}

            {!historyLoading && !historyError && historyItems.length === 0 && (
              <div className="transaction-history-empty">
                Nenhum histórico encontrado para este lançamento.
              </div>
            )}

            {!historyLoading && !historyError && historyItems.length > 0 && (
              <div className="transaction-history-timeline">
                {historyItems.map((item) => (
                  <div key={item.id} className="transaction-history-card">
                    <div className="transaction-history-card-header">
                      <span className="transaction-history-action">
                        {formatHistoryAction(item.action)}
                      </span>
                      <span className="transaction-history-date">
                        {formatAuditDateTime(item.createdAt)}
                      </span>
                    </div>

                    <div className="transaction-history-meta">
                      {formatAuditPerson(item.actor_name, item.actor_role)}
                    </div>

                    {item.details?.description && (
                      <div className="transaction-history-description">
                        {item.details.description}
                      </div>
                    )}

                    {Array.isArray(item.details?.changes) && item.details.changes.length > 0 && (
                      <div className="transaction-history-changes">
                        {item.details.changes.map((change, index) => (
                          <div key={`${item.id}-${change.field}-${index}`} className="transaction-history-change-item">
                            <span className="transaction-history-change-label">
                              {change.label}
                            </span>
                            <span className="transaction-history-change-values">
                              {formatHistoryValue(change.from, change.field)} → {formatHistoryValue(change.to, change.field)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="modal-actions">
            <Button type="button" onClick={() => setIsHistoryModalOpen(false)}>
              Fechar
            </Button>
          </div>
        </Modal>
      )}
    </PageComponent>
  );
}
